import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../components/DashboardLayout";
import { getCourseExamHistory, getMockTestHistory } from "../api/lms/examHistory";
import { getMockTestAttemptReview } from "../api/lms/mock-tests";
import { getAttemptReview } from "../api/lms/exams";
import { format } from "date-fns";
import { Eye, Calendar, Clock, BookOpen, Target, FileText, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../components/ui/button";
import { KCard } from "@/components/KCard";
import { ExamReviewPanel } from "@/components/ExamReviewPanel";
import type { ReviewableQuestion } from "@/components/ExamReviewPanel";

const scoreColor = (pct: number) => {
  if (pct >= 80) return "text-emerald-600";
  if (pct >= 60) return "text-amber-600";
  return "text-red-600";
};

const PassBadge = ({ passed }: { passed: boolean | null }) => {
  if (passed === null) return null;
  return passed ? (
    <span className="dash-badge bg-emerald-50 text-emerald-700 border border-emerald-200">Pass</span>
  ) : (
    <span className="dash-badge bg-red-50 text-red-700 border border-red-200">Fail</span>
  );
};

const formatDate = (d: any) => format(new Date(d), "MMM d, yyyy");

const formatDateTime = (val?: string | Date | null) => {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const formatDuration = (ms: number | null | undefined) => {
  if (!ms) return "—";
  const totalSeconds = Math.floor(ms / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}m ${s}s`;
};

// ── Attempt Detail Modal ────────────────────────────────────────────
function AttemptDetailModal({ attempt, testInfo, type, onClose }: {
  attempt: any;
  testInfo: any;
  type: "course" | "mock";
  onClose: () => void;
}) {
  const pct = testInfo.totalMarks > 0 ? Math.round((attempt.score / testInfo.totalMarks) * 100) : 0;
  const [showReview, setShowReview] = useState(false);

  const reviewQuery = useQuery({
    queryKey: ["attemptReview", type, testInfo.id, attempt.id],
    queryFn: () => {
      if (type === "course") {
        return getAttemptReview(testInfo.courseId, testInfo.id, attempt.id);
      } else {
        return getMockTestAttemptReview(testInfo.id, attempt.id);
      }
    },
    enabled: showReview,
  });

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
        <div className="bg-card rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="sticky top-0 bg-card border-b border-border px-5 py-4 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-foreground">Attempt #{attempt.attemptNumber}</h3>
              <p className="text-xs text-muted-foreground">{testInfo.title}</p>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">✕</button>
          </div>
          <div className="p-5 space-y-4">
            {/* Score summary */}
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-3 rounded-xl bg-muted/30 border border-border">
                <p className={`text-2xl font-bold ${scoreColor(pct)}`}>{attempt.score}</p>
                <p className="text-xs text-muted-foreground">Score</p>
              </div>
              <div className="p-3 rounded-xl bg-muted/30 border border-border">
                <p className="text-2xl font-bold text-foreground">{testInfo.totalMarks}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
              <div className="p-3 rounded-xl bg-muted/30 border border-border">
                <p className={`text-2xl font-bold ${scoreColor(pct)}`}>{pct}%</p>
                <p className="text-xs text-muted-foreground">Percentage</p>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm">
              <div className="space-y-1">
                <p className="text-muted-foreground text-xs">Pass Mark: <span className="text-foreground font-medium">{testInfo.passingMarks || "—"}</span></p>
                <p className="text-muted-foreground text-xs">Duration: <span className="text-foreground font-medium">{formatDuration(attempt.durationMs)}</span></p>
                <p className="text-muted-foreground text-xs">Date: <span className="text-foreground font-medium">{formatDateTime(attempt.submittedAt)}</span></p>
              </div>
            </div>

            {/* Answers summary */}
            {attempt.answers && Object.keys(attempt.answers).length > 0 ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Answer Summary</p>
                  <div className="text-xs text-muted-foreground bg-muted/20 rounded-lg p-3 border border-border">
                    <p>{Object.keys(attempt.answers).length} questions answered</p>
                  </div>
                </div>
                <Button variant="default" size="sm" onClick={() => setShowReview(true)}>
                  <Eye className="w-4 h-4 mr-2" />
                  Review Exam
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Answer Summary</p>
                  <div className="text-xs text-muted-foreground bg-muted/20 rounded-lg p-3 border border-border">
                    <p>No answers recorded</p>
                  </div>
                </div>
                <Button variant="default" size="sm" onClick={() => setShowReview(true)}>
                  <Eye className="w-4 h-4 mr-2" />
                  Review Exam
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showReview && (
        <div className="fixed inset-0 z-[100] bg-[#f4f3f0] overflow-y-auto animate-in fade-in duration-200">
          {reviewQuery.isLoading ? (
            <div className="flex h-full items-center justify-center">
              <div className="animate-spin w-8 h-8 border-4 border-stone-800 border-t-transparent rounded-full" />
            </div>
          ) : reviewQuery.data ? (
            <ExamReviewPanel
              questions={reviewQuery.data as ReviewableQuestion[]}
              answers={(attempt.answers as Record<string, unknown>) || {}}
              attemptId={attempt.id}
              title={`Review: ${testInfo.title} (Attempt #${attempt.attemptNumber})`}
              onClose={() => setShowReview(false)}
              examType={testInfo.examType as any}
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center p-4">
              <p className="text-red-600 mb-4">Could not load review data. {reviewQuery.error ? String(reviewQuery.error) : ""}</p>
              <button onClick={() => setShowReview(false)} className="px-4 py-2 bg-stone-200 rounded">Close</button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export const ExamHistoryPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"course" | "mock">("mock");
  const [expandedTest, setExpandedTest] = useState<string | null>(null);
  const [selectedAttempt, setSelectedAttempt] = useState<{ attempt: any, testInfo: any, type: "course" | "mock" } | null>(null);

  const { data: courseHistoryData, isLoading: courseLoading } = useQuery({
    queryKey: ["course-exam-history"],
    queryFn: () => getCourseExamHistory({ limit: 1000 }),
  });

  const { data: mockHistoryData, isLoading: mockLoading } = useQuery({
    queryKey: ["mock-test-history"],
    queryFn: () => getMockTestHistory({ limit: 1000 }),
  });

  // Groups and sorts mock tests
  const groupedMockTests = useMemo(() => {
    const attempts = mockHistoryData?.items || [];
    const map = new Map<string, { mockTest: any, attempts: any[] }>();
    for (const att of attempts) {
      if (!att.mockTestId) continue;
      if (!map.has(att.mockTestId)) {
        map.set(att.mockTestId, { mockTest: att.mockTest, attempts: [] });
      }
      map.get(att.mockTestId)!.attempts.push(att);
    }
    
    return Array.from(map.values()).map(group => {
      const submitted = group.attempts.filter(a => a.submittedAt);
      submitted.sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()); // Latest first
      
      const scores = submitted.map(a => Number(a.score));
      const totalMarks = group.mockTest?.totalMarks || 1;
      const bestScore = scores.length > 0 ? Math.max(...scores) : null;
      const latestScore = scores.length > 0 ? scores[0] : null;
      const averageScore = scores.length > 0 ? (scores.reduce((a,b) => a+b, 0) / scores.length / totalMarks) * 100 : null;
      
      const passedAttempts = submitted.filter(a => a.isPassed || Number(a.score) >= (group.mockTest?.passingMarks || 0)).length;
      const failedAttempts = submitted.length - passedAttempts;

      return {
        keyId: group.mockTest.id,
        testInfo: group.mockTest,
        attempts: submitted,
        stats: {
          attemptsUsed: submitted.length,
          bestScore,
          latestScore,
          averageScore,
          passedAttempts,
          failedAttempts
        }
      };
    });
  }, [mockHistoryData]);

  // Groups and sorts course exams
  const groupedCourseExams = useMemo(() => {
    const attempts = courseHistoryData?.items || [];
    const map = new Map<string, { exam: any, attempts: any[] }>();
    for (const att of attempts) {
      if (!att.examId) continue;
      if (!map.has(att.examId)) {
        map.set(att.examId, { exam: att.exam, attempts: [] });
      }
      map.get(att.examId)!.attempts.push(att);
    }
    
    return Array.from(map.values()).map(group => {
      const submitted = group.attempts.filter(a => a.submittedAt);
      submitted.sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()); // Latest first
      
      const scores = submitted.map(a => Number(a.score));
      const totalMarks = group.exam?.totalMarks || 1;
      const bestScore = scores.length > 0 ? Math.max(...scores) : null;
      const latestScore = scores.length > 0 ? scores[0] : null;
      const averageScore = scores.length > 0 ? (scores.reduce((a,b) => a+b, 0) / scores.length / totalMarks) * 100 : null;
      
      const passedAttempts = submitted.filter(a => a.isPassed).length;
      const failedAttempts = submitted.length - passedAttempts;

      return {
        keyId: group.exam.id,
        testInfo: group.exam,
        attempts: submitted,
        stats: {
          attemptsUsed: submitted.length,
          bestScore,
          latestScore,
          averageScore,
          passedAttempts,
          failedAttempts
        }
      };
    });
  }, [courseHistoryData]);


  const renderGroupedList = (groups: any[], type: "course" | "mock") => {
    if (groups.length === 0) {
      return (
        <div className="dash-empty-state">
          You haven't taken any {type === "course" ? "course exams" : "mock tests"} yet.
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {groups.map(item => {
          const isOpen = expandedTest === item.keyId;
          const bestPct = item.testInfo?.totalMarks > 0 && item.stats.bestScore !== null
            ? Math.round((item.stats.bestScore / item.testInfo.totalMarks) * 100)
            : null;

          return (
            <KCard key={item.keyId} className="overflow-hidden">
              <button
                onClick={() => setExpandedTest(isOpen ? null : item.keyId)}
                className="w-full p-5 flex items-center gap-4 hover:bg-muted/20 transition-colors text-left"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                  <FileText size={20} className="text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-foreground">{item.testInfo?.title || "Exam"}</p>
                    <div className="flex items-center gap-2 shrink-0">
                      {type === "mock" && item.testInfo?.examType && (
                        <span className="dash-badge bg-secondary text-secondary-foreground">{item.testInfo.examType}</span>
                      )}
                      {type === "mock" && item.testInfo?.level && (
                        <span className="dash-badge bg-secondary text-secondary-foreground">{item.testInfo.level}</span>
                      )}
                      {type === "course" && item.testInfo?.course?.title && (
                        <span className="dash-badge bg-secondary text-secondary-foreground">{item.testInfo.course.title}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                    <span>{item.stats.attemptsUsed} attempts</span>
                    {bestPct !== null && (
                      <>
                        <span>•</span>
                        <span className={`font-semibold ${scoreColor(bestPct)}`}>Best: {bestPct}%</span>
                      </>
                    )}
                  </div>
                </div>

                {isOpen ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
              </button>

              {isOpen && (
                <div className="p-5 space-y-5">
                  {/* Stats row */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {[
                      { label: "Total Attempts", value: item.stats.attemptsUsed },
                      { label: "Passed", value: item.stats.passedAttempts, color: "text-emerald-600" },
                      { label: "Failed", value: item.stats.failedAttempts, color: "text-red-600" },
                      { label: "Avg Score", value: item.stats.averageScore !== null ? `${Math.round(item.stats.averageScore)}%` : "—" },
                      { label: "Best Score", value: item.stats.bestScore !== null ? `${Math.round((item.stats.bestScore / item.testInfo.totalMarks) * 100)}%` : "—", color: item.stats.bestScore !== null ? scoreColor(Math.round((item.stats.bestScore / item.testInfo.totalMarks) * 100)) : "" },
                      { label: "Latest Score", value: item.stats.latestScore !== null ? `${Math.round((item.stats.latestScore / item.testInfo.totalMarks) * 100)}%` : "—", color: item.stats.latestScore !== null ? scoreColor(Math.round((item.stats.latestScore / item.testInfo.totalMarks) * 100)) : "" },
                    ].map((stat, i) => (
                      <div key={i} className="text-center p-3 bg-muted/20 rounded-xl border border-border">
                        <p className={`text-lg font-bold ${stat.color || "text-foreground"}`}>{stat.value}</p>
                        <p className="text-xs text-muted-foreground">{stat.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Attempts List */}
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Attempt History</p>
                    {item.attempts.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-3 bg-muted/10 rounded-xl border border-dashed border-border">No attempts yet</p>
                    ) : (
                      <div className="space-y-3">
                        {item.attempts.map((att: any, idx: number) => {
                          const pct = item.testInfo?.totalMarks > 0
                            ? Math.round((att.score / item.testInfo.totalMarks) * 100)
                            : 0;
                          // Calculate reverse chronological attempt number since we sorted DESC
                          // If there are 5 attempts, the first in the array (idx=0) is attempt #5.
                          const attemptNum = att.attemptNumber || (item.attempts.length - idx);

                          return (
                            <div key={att.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-card rounded-xl border border-border shadow-sm gap-4">
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                  <span className="text-sm font-bold text-primary">#{attemptNum}</span>
                                </div>
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`text-base font-bold ${scoreColor(pct)}`}>
                                      {att.score}/{item.testInfo.totalMarks} ({pct}%)
                                    </span>
                                    <PassBadge passed={att.isPassed} />
                                  </div>
                                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                    <span className="flex items-center gap-1"><Calendar size={12} /> {formatDate(att.submittedAt)}</span>
                                    <span className="flex items-center gap-1"><Clock size={12} /> {formatDuration(att.durationMs)}</span>
                                  </div>
                                </div>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                className="shrink-0 w-full sm:w-auto gap-1.5"
                                onClick={() => setSelectedAttempt({ attempt: att, testInfo: item.testInfo, type })}
                              >
                                <Eye size={14} /> Review Answers
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </KCard>
          );
        })}
      </div>
    );
  };

  return (
    <DashboardLayout showSidebar={true}>
      <div className="dash-page">
        <div className="dash-page-header mb-8">
          <div>
            <h1 className="dash-page-title">My Exam History</h1>
            <p className="dash-page-subtitle">Review your past course exams and mock tests.</p>
          </div>
        </div>

        {/* Tab Switcher matching the app's aesthetic */}
        <div className="flex gap-2 mb-6 border-b border-border pb-1 overflow-x-auto">
          <button
            onClick={() => { setActiveTab("mock"); setExpandedTest(null); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${activeTab === "mock" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"}`}
          >
            Mock Tests
          </button>
          <button
            onClick={() => { setActiveTab("course"); setExpandedTest(null); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${activeTab === "course" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"}`}
          >
            Course Exams
          </button>
        </div>

        {activeTab === "mock" && (
          <div>
            {mockLoading ? (
              <div className="text-center py-10 text-muted-foreground text-sm">Loading history...</div>
            ) : (
              renderGroupedList(groupedMockTests, "mock")
            )}
          </div>
        )}

        {activeTab === "course" && (
          <div>
            {courseLoading ? (
              <div className="text-center py-10 text-muted-foreground text-sm">Loading history...</div>
            ) : (
              renderGroupedList(groupedCourseExams, "course")
            )}
          </div>
        )}
      </div>

      {selectedAttempt && (
        <AttemptDetailModal
          attempt={selectedAttempt.attempt}
          testInfo={selectedAttempt.testInfo}
          type={selectedAttempt.type}
          onClose={() => setSelectedAttempt(null)}
        />
      )}
    </DashboardLayout>
  );
};
